import React, { useState, useEffect } from 'react';
import { FaUsers, FaUser, FaArrowLeft } from 'react-icons/fa';
import SelectField from '../components/SelectField';
import LoadingState from '../components/LoadingState';
import { ContactTable } from '../components/ContactTable';
import { getAllCampaigns, list } from '../api/api';
import { getAssignmentsForUser, getAssignments, setAssignment } from '../common/assignments';
import { listCognitoUsers } from '../api/users';
import { getMyTeam } from '../api/agentEmails';
import ConfirmationModal from '../components/ConfirmationModal';
import { payloadFromToken } from '../api/auth';
import { devLog } from '../common/devLog';

// The same group names useIsCallAgent accepts. The groups claim may be a single name or a list.
const isCallAgentGroup = (groups) =>
  (Array.isArray(groups) ? groups : [groups]).some((g) => g === 'CallAgent' || g === 'CallAgents');

const CallAgents = ({ setAlert, username }) => {
  const [assignmentsState, setAssignmentsState] = useState(getAssignments());
  const [users] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("None");
  const [assessed] = useState("Assessed")
  const [isTransitioning, setIsTransitioning] = useState(false);
  const userToken = localStorage.getItem("id_token");
  const parsedToken = payloadFromToken(userToken);
  const cognitoEmail = parsedToken["email"] || "Unknown";
  const cognitoGrp = parsedToken["cognito:groups"] || "Unknown";

  // Load more pagination state
  const [data, setData] = useState(null);
  // Starts true: nothing is shown as "no calls" until the first load (which waits on ownAgentName) ends.
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [startKey, setStartKey] = useState(null);
  const [campaignAssignedTo, setCampaignAssignedTo] = useState(null);
  const [error, setError] = useState(null);

  const [confirmation, setConfirmation] = useState({
    open: false,
    title: '',
    message: ''
  });

  // A General Manager can switch between their own calls and those of each agent on their team.
  // `viewing` is '' for the signed-in user's own calls, otherwise a team member's name. The server
  // enforces the same team, so this list only decides what is offered.
  const [team, setTeam] = useState([]);
  // A GM manages their team on the Agents page (2026-10-20), linked from here even before it has members.
  const [isGm, setIsGm] = useState(false);
  const [viewing, setViewing] = useState('');
  // The name this user's own calls are filed under: the agent an admin linked their login to, or
  // their username. Calls load only once it is known, so a linked user never queries the wrong name.
  const [ownAgentName, setOwnAgentName] = useState(null);
  const viewingAgent = viewing || ownAgentName || username;

  useEffect(() => {
    getAllCampaigns()
      .then((data) => setCampaigns(data || []))
      .catch((err) => console.error('Failed to load campaigns', err));
  }, []);

  useEffect(() => {
    getMyTeam()
      .then((res) => {
        setTeam(res && res.role === 'GM' ? res.members || [] : []);
        setIsGm(Boolean(res && res.role === 'GM'));
        setOwnAgentName((res && res.agentName) || username);
      })
      .catch((err) => {
        // Without a team or a link the page is an ordinary My Calls page under the username.
        console.error('Failed to load team', err);
        setTeam([]);
        setOwnAgentName(username);
      });
  }, [username]);

  // Request parameters shared by the first page and "Load more", so the two cannot drift apart.
  const baseParams = () => {
    const params = {
      count: window.pcaSettings?.api?.pageSize || 25,
      username: viewingAgent
    };
    if (isCallAgentGroup(cognitoGrp)) {
      params.callAgent = true;
    }
    // Only add campaign if it's not "None"
    if (selectedCampaign !== "None") {
      params.campaign = selectedCampaign;
    }
    return params;
  };

  // Load calls when campaign changes
  useEffect(() => {
    const loadCalls = async () => {
      if (!ownAgentName) return;   // still resolving whose calls "my calls" are

      setIsLoading(true);
      setError(null);

      try {
        const params = baseParams();
        devLog('PARAMS', params);
        const response = await list(params);
        devLog('Loaded calls', response);
        setData(response);
        setStartKey(response.StartKey || null);
        setCampaignAssignedTo(response.campaignAssignedTo || null);
      } catch (err) {
        console.error('Failed to load calls', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, username, assessed, viewing, ownAgentName]);

  // Load more calls function
  const loadMore = async () => {
    if (!startKey || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      // Same parameters as the first page. "Load more" used to omit the call-agent flag, so a call
      // agent's second page fell through to the QA "assigned to" query and showed the wrong calls.
      const params = {
        ...baseParams(),
        startKey: startKey,
        campaignAssignedTo: campaignAssignedTo
      };

      const response = await list(params);

      // Append new records to existing data
      setData(prev => ({
        ...response,
        Records: [...(prev?.Records || []), ...(response.Records || [])]
      }));

      setStartKey(response.StartKey || null);
      setCampaignAssignedTo(response.campaignAssignedTo || null);
    } catch (err) {
      console.error('Failed to load more calls', err);
      setAlert({
        variant: 'error',
        heading: 'Error',
        text: 'Failed to load more calls.'
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleAssign = (item, user) => {
    try {
      setAssignment(item.key || item.filename, user);
      setAssignmentsState(getAssignments());
      setConfirmation({
        open: true,
        title: 'Assignment Updated',
        message: user
          ? `Agent ${user} assigned successfully.`
          : 'Assignment removed.'
      });
    } catch (e) {
      setConfirmation({
        open: true,
        title: 'Error',
        message: 'Failed to assign agent.'
      });
    }
  };

  const handleCampaignChange = (campaignValue) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedCampaign(campaignValue);
      setIsTransitioning(false);
    }, 150);
  };

  const handleViewingChange = (member) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setViewing(member);
      setIsTransitioning(false);
    }, 150);
  };

//   const handleAssessedChange = (assessedValue) => {
//     setIsTransitioning(true);
//     setTimeout(() => {
//       setAssessed(assessedValue);
//       setIsTransitioning(false);
//     }, 150);
//   }

  useEffect(() => {
    if (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load calls.' });
    }
  }, [error, setAlert]);

  // Group calls by some identifier (you can modify this based on your data structure)
  const groupedCalls = React.useMemo(() => {
    if (!data?.Records) return [];
    return data.Records.filter(call => call.assessed === true);
  }, [data]);

  // Show transition loading overlay
  if (isTransitioning) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Assigned to Me</h1>
        
        <LoadingState label="Loading campaign data..." />
      </div>
    );
  }

  // SelectField's className REPLACES its default styling rather than adding to it, so `select-field` has
  // to be passed explicitly; leaving it out strips the background, chevron and dark-mode colours.
  const campaignSelect = (
    <SelectField
      id="campaign"
      className="select-field select-field--accent w-full"
      value={selectedCampaign}
      onChange={(e) => handleCampaignChange(e.target.value)}
    >
      <option value="None">All Campaigns</option>
      {campaigns.campaigns?.map(campaign => (
        <option key={campaign.campaignName} value={campaign.campaignName}>
          {campaign.campaignName}
        </option>
      ))}
    </SelectField>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{viewing ? `${viewing}'s Calls` : 'Assigned to Me'}</h1>
        {viewing && (
          <p className="text-sm text-gray-500 mt-1">A member of your team</p>
        )}
      </div>

      {team.length > 0 ? (
        // A General Manager's team panel: the one place to switch whose calls are shown. Set apart from
        // the page so it reads as the main control, not one filter among several.
        <section className="card border-cloudza-200 bg-cloudza-50/60 space-y-4" aria-labelledby="team-heading">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                <FaUsers />
              </span>
              <div className="min-w-0">
                <h2 id="team-heading" className="text-base font-semibold text-gray-900">Your team</h2>
                <p className="text-sm text-gray-600">View your own calls, or choose an agent on your team to view theirs.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white border border-cloudza-300 text-cloudza-700 text-xs font-semibold whitespace-nowrap">
                {team.length} agent{team.length === 1 ? '' : 's'}
              </span>
              <a href="/agents" className="btn-outline !py-1 !px-3 !text-xs whitespace-nowrap">
                <FaUsers /> Manage my team
              </a>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[16rem] max-w-md">
              <label htmlFor="team-member" className="block text-sm font-semibold text-gray-800 mb-1">Viewing calls for</label>
              <SelectField
                id="team-member"
                className="select-field select-field--accent w-full"
                value={viewing}
                onChange={(e) => handleViewingChange(e.target.value)}
              >
                <option value="">My calls</option>
                {team.map((member) => (
                  <option key={member} value={member}>{member}</option>
                ))}
              </SelectField>
            </div>
            <div className="w-full sm:w-56">
              <label htmlFor="campaign" className="block text-sm font-semibold text-gray-800 mb-1">Campaign</label>
              {campaignSelect}
            </div>
          </div>

          {/* Says plainly whose calls are on screen, so a GM never mistakes a team member's results for
              their own, and gives a one-click way back. */}
          {viewing && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white border border-cloudza-300 px-4 py-2.5">
              <p className="text-sm text-gray-700 flex items-center gap-2 min-w-0">
                <FaUser className="text-cloudza-600 flex-shrink-0" />
                <span className="truncate">Showing calls for <b className="text-gray-900">{viewing}</b></span>
              </p>
              <button type="button" onClick={() => handleViewingChange('')} className="btn-outline !py-1 !px-3 !text-xs">
                <FaArrowLeft /> Back to my calls
              </button>
            </div>
          )}
        </section>
      ) : (
        <>
          {isGm && (
            <div className="card flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                You're a General Manager. Add agents to your team to see their calls here.
              </p>
              <a href="/agents" className="btn-outline !py-1 !px-3 !text-xs whitespace-nowrap">
                <FaUsers /> Manage my team
              </a>
            </div>
          )}
          <div className="w-full sm:w-56">{campaignSelect}</div>
        </>
      )}

      {isLoading ? (
        <LoadingState variant="rows" header={false} label="Loading calls" />
      ) : (
        <ContactTable
          email={cognitoEmail}
          group={cognitoGrp}
          data={groupedCalls || []}
          loading={isLoading}
          assignments={assignmentsState}
          users={users}
          onAssign={handleAssign}
          empty={<div className="text-center py-8 text-gray-500">
            {viewing ? `No assessed calls found for ${viewing} in this campaign.` : 'No assigned calls found for this campaign.'}
          </div>}
          onRowClick={(item) => window.open(`/dashboard/${item.key || item.filename}`, '_blank', 'noopener,noreferrer')}
          onLoadMore={loadMore}
          hasMore={!!startKey}
          isLoadingMore={isLoadingMore}
        />
      )}

      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => setConfirmation({ ...confirmation, open: false })}
      />
    </div>
  );
};

export default CallAgents;